import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HelpIcon from '@mui/icons-material/Help';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import KeyboardArrowRightOutlinedIcon from '@mui/icons-material/KeyboardArrowRightOutlined';
import PlayCircleFilledWhiteOutlinedIcon from '@mui/icons-material/PlayCircleFilledWhiteOutlined';
import { Fragment, type ReactElement } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { r, s } from '@/theme/tokens';
import { uiTokens } from '@/theme/uiTokens';
import { TableCellContainer } from '@/ui/composites/table';
import { OverflowTooltip } from '@/ui/composites/table/cells/OverflowTooltip';
import { Stack } from '@/ui/layout/Stack';
import {
  Box,
  darken,
  ErrorOutline,
  formatStatusLabel,
  IconButton,
  Link,
  TableCell,
  TableRow,
  Text,
  Tooltip,
} from '@/ui/primitives';
import type {
  DailyHistoryCell,
  DailyStatus,
  EntityDailyHistoryRow,
} from './EntityDailyHistoryPanel.types';
import type { SxProps } from '@/ui/primitives/types';
import type { SvgIconComponent } from '@mui/icons-material';
import type { Theme } from '@mui/material/styles';

type NonArraySx = Exclude<SxProps<Theme>, readonly unknown[]>;

const HISTORY_TOOLTIP_SLOT_PROPS = {
  tooltip: {
    sx: {
      maxWidth: 'none',
      width: 'fit-content',
      fontSize: '15px',
      lineHeight: 1.4,
      p: 0,
    },
  },
} as const;
const HISTORY_TOOLTIP_ENTER_DELAY_MS = 250;
const CHILD_NAME_INDENT = s('sm');
const EXPAND_ICON_GUTTER_WIDTH = '32px';
const HISTORY_NAME_CONTENT_SX = {
  display: 'flex',
  alignItems: 'center',
  minWidth: 0,
  width: '100%',
  maxWidth: '100%',
  flex: 1,
  overflow: 'hidden',
} as const;

const buildHistoryNameTitle = (row: EntityDailyHistoryRow): string =>
  row.prefixLabel ? `${row.prefixLabel} / ${row.name}` : row.name;

const getTooltipStatusIcon = (status: Exclude<DailyStatus, null>): SvgIconComponent => {
  if (status === 'passed' || status === 'completed') return CheckCircleOutlineIcon;
  if (status === 'failed') return CancelOutlinedIcon;
  if (status === 'errored') return ErrorOutline;
  if (status === 'running') return PlayCircleFilledWhiteOutlinedIcon;
  if (status === 'skipped') return BlockOutlinedIcon;
  return HelpIcon;
};

const resolveDailyStatusBaseColor = (status: DailyStatus): string => {
  switch (status) {
    case null:
      return 'transparent';
    case 'passed':
    case 'completed':
      return uiTokens.statusColors.green;
    case 'failed':
      return uiTokens.statusColors.red;
    case 'errored':
      return uiTokens.statusColors.brown;
    case 'skipped':
      return uiTokens.statusColors.orange;
    case 'running':
      return uiTokens.statusColors.blue;
    case 'queued':
      return uiTokens.statusColors.neutral;
    default:
      return 'transparent';
  }
};

const resolveDailyStatusBorderColor = (status: DailyStatus): string =>
  status === null ? 'divider' : 'transparent';

const resolveDailyStatusColor = (status: DailyStatus): string => {
  const baseColor = resolveDailyStatusBaseColor(status);
  if (status === null) return baseColor;
  return darken(baseColor, 0.26);
};

const formatHistoryTooltipDate = (value: Date): string =>
  new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(value);

const formatHistoryTooltipTime = (value: Date): string =>
  new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(value);

const renderHistoryTooltip = (
  historyCell: DailyHistoryCell,
  activityLabel: 'executions' | 'builds',
): ReactElement => (
  <Box sx={{ width: 'fit-content', minWidth: 'fit-content' }}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: s('sm'),
        px: s('md'),
        py: s('sm'),
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Text
        role="body"
        sx={{
          fontSize: (theme) => theme.typography.pxToRem(15),
          fontWeight: (theme) => theme.typography.fontWeightRegular,
        }}
      >
        {formatHistoryTooltipDate(historyCell.day)}
      </Text>
    </Box>
    <Stack gap="xxs" sx={{ px: s('md'), py: s('sm') }}>
      {historyCell.details.length === 0 ? (
        <Text
          role="body"
          sx={{
            color: 'text.secondary',
            fontSize: (theme) => theme.typography.pxToRem(15),
            whiteSpace: 'nowrap',
          }}
        >
          {activityLabel === 'builds' ? 'No builds' : 'No executions'}
        </Text>
      ) : (
        historyCell.details.map((detail, detailIndex) => {
          const StatusIcon = getTooltipStatusIcon(detail.status);
          return (
            <Box
              key={`${historyCell.day.toISOString()}-${String(detailIndex)}`}
              sx={{
                display: 'grid',
                gridTemplateColumns: 'auto auto minmax(0, 1fr)',
                alignItems: 'baseline',
                gap: s('md'),
                minWidth: 0,
              }}
            >
              <Text
                role="body"
                sx={{
                  color: 'text.secondary',
                  fontSize: (theme) => theme.typography.pxToRem(15),
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                {formatHistoryTooltipTime(detail.timestamp)}
              </Text>
              <Box
                component={StatusIcon}
                sx={{
                  fontSize: (theme) => theme.app.iconSizes.sm,
                  flexShrink: 0,
                  transform: 'translateY(2px)',
                  color: resolveDailyStatusBaseColor(detail.status),
                }}
              />
              <Text
                role="body"
                sx={{
                  fontSize: (theme) => theme.typography.pxToRem(15),
                  whiteSpace: 'nowrap',
                  pr: s('sm'),
                }}
              >
                {detail.runHref && detail.runLabel ? (
                  <Link
                    component={RouterLink}
                    to={detail.runHref}
                    underline="hover"
                    color="inherit"
                    sx={{
                      display: 'inline-block',
                      width: 'fit-content',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {detail.runLabel}
                  </Link>
                ) : (
                  (detail.runLabel ?? formatStatusLabel(detail.status))
                )}
              </Text>
            </Box>
          );
        })
      )}
    </Stack>
  </Box>
);

const HistoryStatusesGrid = ({
  rowId,
  dailyHistory,
  blockSize,
  activityLabel,
}: {
  rowId: string;
  dailyHistory: DailyHistoryCell[];
  blockSize: number;
  activityLabel: 'executions' | 'builds';
}): ReactElement => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: `repeat(${String(dailyHistory.length)}, minmax(0, 1fr))`,
      columnGap: s('md'),
      alignItems: 'center',
      width: '100%',
      minWidth: 0,
    }}
  >
    {dailyHistory.map((historyCell) => (
      <Tooltip
        key={`${rowId}-${historyCell.day.toISOString()}`}
        title={renderHistoryTooltip(historyCell, activityLabel)}
        arrow
        enterDelay={HISTORY_TOOLTIP_ENTER_DELAY_MS}
        enterNextDelay={HISTORY_TOOLTIP_ENTER_DELAY_MS}
        slotProps={HISTORY_TOOLTIP_SLOT_PROPS}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            minWidth: 0,
            width: '100%',
          }}
        >
          <Box
            sx={{
              width: '100%',
              minWidth: `${blockSize}px`,
              height: `${blockSize}px`,
              borderRadius: r('sm'),
              border: '1px solid',
              borderColor: resolveDailyStatusBorderColor(historyCell.status),
              backgroundColor: 'transparent',
              display: 'flex',
              overflow: 'hidden',
            }}
          >
            {historyCell.statuses.length === 0
              ? null
              : historyCell.statuses.map((status, statusIndex) => (
                  <Box
                    key={`${rowId}-${historyCell.day.toISOString()}-${String(statusIndex)}`}
                    sx={{
                      flex: '1 1 0',
                      height: '100%',
                      backgroundColor: resolveDailyStatusColor(status),
                      borderLeft: statusIndex === 0 ? 'none' : '1px solid',
                      borderColor: statusIndex === 0 ? 'transparent' : 'background.paper',
                    }}
                  />
                ))}
          </Box>
        </Box>
      </Tooltip>
    ))}
  </Box>
);

const HistoryNameCell = ({
  row,
  rowNameTextSx,
  childRowIndent,
  nameColor = 'inherit',
  showExpand,
  isExpanded,
  onToggle,
}: {
  row: EntityDailyHistoryRow;
  rowNameTextSx: NonArraySx;
  childRowIndent?: string;
  nameColor?: string;
  showExpand?: boolean;
  isExpanded?: boolean;
  onToggle?: () => void;
}): ReactElement => {
  const fullNameTitle = buildHistoryNameTitle(row);

  return (
    <TableCellContainer
      gap="none"
      paddingTop="none"
      sx={{
        minHeight: '30px',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          minWidth: 0,
          width: '100%',
          pl: childRowIndent ?? '0',
        }}
      >
        <Box sx={HISTORY_NAME_CONTENT_SX}>
          {row.prefixLabel ? (
            row.prefixHref ? (
              <Link
                component={RouterLink}
                to={row.prefixHref}
                underline="hover"
                color="inherit"
                sx={[
                  rowNameTextSx,
                  { color: 'text.secondary', whiteSpace: 'nowrap', flexShrink: 0 },
                ]}
              >
                {row.prefixLabel}
              </Link>
            ) : (
              <Text
                role="body"
                sx={[
                  rowNameTextSx,
                  { color: 'text.secondary', whiteSpace: 'nowrap', flexShrink: 0 },
                ]}
              >
                {row.prefixLabel}
              </Text>
            )
          ) : null}
          {row.prefixLabel ? (
            <Text
              role="body"
              sx={[rowNameTextSx, { color: 'text.secondary', flexShrink: 0, mx: s('sm') }]}
            >
              /
            </Text>
          ) : null}
          <OverflowTooltip
            title={fullNameTitle}
            wrapperSx={{ flex: 1, minWidth: 0, maxWidth: '100%' }}
          >
            <Link
              component={RouterLink}
              to={row.href}
              underline="hover"
              color={nameColor}
              sx={[
                rowNameTextSx,
                {
                  display: 'block',
                  width: '100%',
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
              ]}
            >
              {row.name}
            </Link>
          </OverflowTooltip>
        </Box>
        {showExpand && onToggle ? (
          <IconButton
            aria-label={isExpanded ? `Collapse ${row.name}` : `Expand ${row.name}`}
            onClick={onToggle}
            sx={{
              color: 'text.secondary',
              ml: s('sm'),
              flexShrink: 0,
              width: '32px',
              height: '32px',
              p: s('xxs'),
            }}
          >
            {isExpanded ? (
              <KeyboardArrowDownOutlinedIcon
                sx={{
                  fontSize: (theme) => theme.typography.pxToRem(26),
                  width: '26px',
                  height: '26px',
                }}
              />
            ) : (
              <KeyboardArrowRightOutlinedIcon
                sx={{
                  fontSize: (theme) => theme.typography.pxToRem(26),
                  width: '26px',
                  height: '26px',
                }}
              />
            )}
          </IconButton>
        ) : showExpand ? (
          <Box sx={{ width: EXPAND_ICON_GUTTER_WIDTH, ml: s('sm'), flexShrink: 0 }} />
        ) : null}
      </Box>
    </TableCellContainer>
  );
};

type EntityDailyHistoryRowsProps = {
  rows: EntityDailyHistoryRow[];
  expandedRowIds: Set<string>;
  onToggleRowExpansion: (rowId: string) => void;
  rowHeight: number;
  blockSize: number;
  nameColumnWidth: string;
  rowNameTextSx: NonArraySx;
  activityLabel: 'executions' | 'builds';
};

export const EntityDailyHistoryRows = ({
  rows,
  expandedRowIds,
  onToggleRowExpansion,
  rowHeight,
  blockSize,
  nameColumnWidth,
  rowNameTextSx,
  activityLabel,
}: EntityDailyHistoryRowsProps): ReactElement => (
  <>
    {rows.map((row) => {
      const isExpanded = expandedRowIds.has(row.id);
      const hasChildren = Boolean(row.childRows && row.childRows.length > 0);
      const childGridFirstColumnWidth = nameColumnWidth;
      return (
        <Fragment key={row.id}>
          <TableRow
            sx={{
              height: `${rowHeight}px`,
            }}
          >
            <TableCell
              sx={{
                width: nameColumnWidth,
                minWidth: nameColumnWidth,
                maxWidth: nameColumnWidth,
                px: s('md'),
                py: s('none'),
                verticalAlign: 'middle',
                borderBottom: 'none',
              }}
            >
              <HistoryNameCell
                row={row}
                rowNameTextSx={rowNameTextSx}
                showExpand={hasChildren}
                isExpanded={isExpanded}
                onToggle={() => {
                  onToggleRowExpansion(row.id);
                }}
              />
            </TableCell>
            <TableCell
              sx={{
                px: s('md'),
                py: s('none'),
                verticalAlign: 'middle',
                borderBottom: 'none',
                width: '100%',
              }}
            >
              <HistoryStatusesGrid
                rowId={row.id}
                dailyHistory={row.dailyHistory}
                blockSize={blockSize}
                activityLabel={activityLabel}
              />
            </TableCell>
          </TableRow>
          {hasChildren ? (
            <TableRow sx={{ '& td': { borderBottom: 'none' } }}>
              <TableCell colSpan={2} sx={{ p: s('none'), borderBottom: 'none' }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateRows: isExpanded ? '1fr' : '0fr',
                    transition: (muiTheme) =>
                      muiTheme.transitions.create(['grid-template-rows', 'opacity'], {
                        duration: muiTheme.transitions.duration.shorter,
                        easing: muiTheme.transitions.easing.easeInOut,
                      }),
                    opacity: isExpanded ? 1 : 0,
                  }}
                >
                  <Box
                    sx={{
                      overflow: 'hidden',
                    }}
                  >
                    <Box aria-label={`${row.name} history children`}>
                      {(row.childRows ?? []).map((childRow) => (
                        <Box
                          key={childRow.id}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: `${childGridFirstColumnWidth} minmax(
                              0,
                              1fr
                            )`,
                            alignItems: 'center',
                            minHeight: `${rowHeight}px`,
                          }}
                        >
                          <Box
                            sx={{
                              width: childGridFirstColumnWidth,
                              px: s('md'),
                              py: s('none'),
                              minWidth: nameColumnWidth,
                              maxWidth: nameColumnWidth,
                              overflow: 'hidden',
                            }}
                          >
                            <HistoryNameCell
                              row={childRow}
                              rowNameTextSx={rowNameTextSx}
                              childRowIndent={CHILD_NAME_INDENT}
                              nameColor="text.secondary"
                              showExpand
                            />
                          </Box>
                          <Box
                            sx={{
                              px: s('md'),
                              py: s('none'),
                              pl: `calc(${s('md')} + 4px)`,
                              minWidth: 0,
                              width: '100%',
                            }}
                          >
                            <HistoryStatusesGrid
                              rowId={childRow.id}
                              dailyHistory={childRow.dailyHistory}
                              blockSize={blockSize}
                              activityLabel={activityLabel}
                            />
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              </TableCell>
            </TableRow>
          ) : null}
        </Fragment>
      );
    })}
  </>
);
